/**
 * Impersonation Banner
 *
 * Persistent indicator shown when an admin is impersonating another user.
 * Displays the impersonated user's name and provides a "Stop" action
 * to end impersonation and restore the admin session.
 */
import { useCallback, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { getCsrfToken } from "../../lib/csrf";
import { toast } from "../../lib/toast";

export const ImpersonationBanner = () => {
  const { user, impersonation } = useAuth();
  const [ending, setEnding] = useState(false);

  const handleStopImpersonation = useCallback(async () => {
    setEnding(true);
    try {
      const csrfToken = getCsrfToken();
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (csrfToken) headers["x-csrf-token"] = csrfToken;

      const res = await fetch("/api/auth/end-impersonation", {
        method: "POST",
        headers,
        body: JSON.stringify({}),
      });

      const body = await res.json();
      if (res.ok && body.ok) {
        const redirectUrl = body.data?.redirectUrl ?? "/users/admin";
        window.location.href = redirectUrl;
      } else {
        toast.error(body.message ?? "Failed to end impersonation");
        setEnding(false);
      }
    } catch {
      toast.error("Network error while ending impersonation");
      setEnding(false);
    }
  }, []);

  if (!impersonation?.active || !user) return null;

  return (
    <div
      className="mb-6 rounded-lg border border-blue-500/40 bg-blue-500/10 px-4 py-3 text-sm text-blue-900 dark:text-blue-200"
      role="status"
      aria-live="polite"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <strong className="font-semibold">Impersonating:</strong>{" "}
          You are viewing the dashboard as <strong>{user.username}</strong>.
        </div>
        <button
          type="button"
          onClick={() => void handleStopImpersonation()}
          disabled={ending}
          className="rounded-md bg-blue-600 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white transition-colors hover:bg-blue-700 disabled:opacity-50 dark:bg-blue-500 dark:hover:bg-blue-600"
        >
          {ending ? "Ending..." : "Stop Impersonating"}
        </button>
      </div>
    </div>
  );
};
